import { useState, useRef, useCallback } from "react";
import { CheckCircle2, Loader2, X, Zap, Clock, Coins, AlertCircle } from "lucide-react";



export const PipelineRunner = ({ orchestration, onClose, onComplete }) => {
  const [status, setStatus] = useState("idle"); // idle | running | completed | error
  const [agents, setAgents] = useState(
    (orchestration?.agents || [])?.map(name => ({ name, status: "pending", progress: 0, time: "—", tokens: 0, cost: 0, outputs: 0, output_desc: "" }))
  );
  const [totals, setTotals] = useState({ tokens: 0, cost: 0, duration: "—" });
  const [error, setError] = useState(null);
  const eventSourceRef = useRef(null);

  const startRun = useCallback(() => {
    setStatus("running");
    setError(null);

    const evtSource = new EventSource(`${API}/orchestrations/${orchestration.id}/run`, { withCredentials: true });
    eventSourceRef.current = evtSource;

    evtSource.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data);
        const { type, data } = parsed;

        if (type === "agent_started") {
          setAgents(prev => prev?.map((a, i) => i === data.index ? { ...a, status: "in_progress" } : a));
        } else if (type === "agent_progress") {
          setAgents(prev => prev?.map((a, i) => i === data.index ? { ...a, progress: data.progress } : a));
        } else if (type === "agent_completed") {
          setAgents(prev => prev?.map((a, i) => i === data.index ? {
            ...a, status: "completed", progress: 100,
            time: data.time, tokens: data.tokens, cost: data.cost,
            outputs: data.outputs, output_desc: data.output_desc,
          } : a));
        } else if (type === "run_completed") {
          setTotals({ tokens: data.totalTokens, cost: data.totalCost, duration: data.duration });
          setStatus("completed");
          evtSource.close();
          onComplete?.();
        } else if (type === "error") {
          setError(data);
          setStatus("error");
          evtSource.close();
        }
      } catch (e) { /* ignore parse errors */ }
    };

    evtSource.onerror = () => {
      if (status === "running") {
        setError("Connection lost");
        setStatus("error");
      }
      evtSource.close();
    };
  }, [orchestration, onComplete, status]);

  const handleClose = () => {
    eventSourceRef.current?.close();
    onClose();
  };

  const completedCount = agents?.filter(a => a.status === "completed").length;
  const runningAgent = agents.find(a => a.status === "in_progress");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" data-testid="pipeline-runner-overlay">
      <div className="bg-white rounded-2xl shadow-elevated border border-border/60 w-full max-w-lg max-h-[85vh] flex flex-col" data-testid="pipeline-runner-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border/40">
          <div>
            <h3 className="font-heading font-bold text-sm text-foreground">{orchestration.name}</h3>
            <p className="text-[11px] text-foreground/40 mt-0.5">
              {status === "idle" && `${agents.length} agents ready`}
              {status === "running" && `Running ${runningAgent?.name || ""}... (${completedCount}/${agents.length})`}
              {status === "completed" && `Completed in ${totals.duration}`}
              {status === "error" && "Execution failed"}
            </p>
          </div>
          <button onClick={handleClose} className="w-7 h-7 rounded-lg text-foreground/50 hover:text-foreground hover:bg-foreground/5 flex items-center justify-center" data-testid="close-runner">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Overall progress bar */}
        {status !== "idle" && (
          <div className="px-6 pt-3">
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${status === "error" ? "bg-red-500" : status === "completed" ? "bg-emerald-500" : "bg-[#014d7d]"}`}
                style={{ width: `${(completedCount / agents.length) * 100}%` }}
              />
            </div>
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-[10px] text-foreground/40">{completedCount} of {agents.length} agents</span>
              {status === "completed" && (
                <div className="flex items-center gap-3 text-[10px] text-foreground/40">
                  <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> {(totals.tokens / 1000).toFixed(1)}K tokens</span>
                  <span className="flex items-center gap-1"><Coins className="w-3 h-3" /> ${totals.cost.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Agent list */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
          {agents?.map((agent, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${
                agent.status === "completed" ? "border-emerald-200 bg-emerald-50/30" :
                agent.status === "in_progress" ? "border-[#014d7d]/30 bg-[#014d7d]/5" :
                "border-border/40 bg-white"
              }`}
              data-testid={`runner-agent-${i}`}
            >
              {/* Status icon */}
              <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0">
                {agent.status === "completed" && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                {agent.status === "in_progress" && <Loader2 className="w-4 h-4 text-[#014d7d] animate-spin" />}
                {agent.status === "pending" && <div className="w-4 h-4 rounded-full border-2 border-foreground/15" />}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold ${agent.status === "completed" ? "text-emerald-700" : agent.status === "in_progress" ? "text-[#014d7d]" : "text-foreground/40"}`}>
                    {agent.name}
                  </span>
                  {agent.status === "in_progress" && (
                    <span className="text-[10px] text-[#014d7d]/60">{agent.progress}%</span>
                  )}
                </div>
                {agent.status === "in_progress" && (
                  <div className="h-1 bg-[#014d7d]/10 rounded-full mt-1.5 overflow-hidden">
                    <div className="h-full bg-[#014d7d] rounded-full transition-all duration-300" style={{ width: `${agent.progress}%` }} />
                  </div>
                )}
                {agent.status === "completed" && (
                  <div className="flex items-center gap-3 mt-1 text-[10px] text-emerald-600/70">
                    <span>{agent.time}</span>
                    <span>{(agent.tokens / 1000).toFixed(1)}K tokens</span>
                    <span>${agent.cost.toFixed(2)}</span>
                    <span>{agent.outputs} outputs</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mb-3 flex items-start gap-2 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
            <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
            <p className="text-xs text-red-600">{error}</p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border/40">
          {status === "idle" && (
            <button onClick={startRun} className="text-xs font-semibold px-5 py-2 rounded-xl bg-[#014d7d] text-white hover:bg-[#013d66] flex items-center gap-2" data-testid="start-run-btn">
              <Zap className="w-3.5 h-3.5" /> Start Execution
            </button>
          )}
          {status === "completed" && (
            <button onClick={handleClose} className="text-xs font-semibold px-5 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-2" data-testid="done-run-btn">
              <CheckCircle2 className="w-3.5 h-3.5" /> Done
            </button>
          )}
          {status === "running" && (
            <span className="text-xs text-foreground/40 flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Executing pipeline...
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
